import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../ui/badge';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { doGetIssue, doGetIssues, doMarkAllAsSeen, doMarkAsSeen } from '../../modules/issues/stores/thunk';
import {
  selectActiveIssues,
  selectActiveIssuesPagination,
  selectActiveUnseenCount,
  selectIssueById,
  selectResolvedIssues,
  selectResolvedIssuesPagination,
  selectResolvedUnseenCount,
} from '../../modules/issues/stores/slice';
import { formatLastUpdated } from './Home/ActiveIssues';
import IssueChat from './Issues/IssueChat';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { AppDispatch } from '../../redux/store';
import { Modal, Spin } from 'antd';
import { Bike, CheckCheck, Hash, IdCard, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { RelatedIssue } from './Issues/RelatedIssue';

import ReportIssueForm from '../Forms/Issues/ReportIssueForm';
import { ROLES } from '../../constants';

const IssuesPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();
  const location = useLocation();

  const [reportIssueModalOpen, setReportIssueModalOpen] = useState(false);
  const user = useSelector((state: any) => state.auth.user);
  const role = useSelector((state: any) => state.auth.user.role);
  const userId = useSelector((state: { auth: { user: { id: string } } }) => state.auth.user.id);
  const activeIssues = useSelector((state) => selectActiveIssues(state));
  const resolvedIssues = useSelector((state) => selectResolvedIssues(state));
  const isFetchingIssues = useSelector((state: { issues: { isFetching: boolean } }) => state.issues.isFetching);
  const [currentTab, setCurrentTab] = useState('active');
  const displayedIssues = currentTab === 'active' ? activeIssues : resolvedIssues;

  const [selectedIssue, setSelectedIssue] = useState(null);

  const activeIssuesPagination = useSelector((state) => selectActiveIssuesPagination(state));
  const resolvedIssuesPagination = useSelector((state) => selectResolvedIssuesPagination(state));
  const activeUnseenCount = useSelector((state) => selectActiveUnseenCount(state));
  const resolvedUnseenCount = useSelector((state) => selectResolvedUnseenCount(state));

  const pagination = currentTab === 'active' ? activeIssuesPagination : resolvedIssuesPagination;

  const scrollContainerRef = useRef(null);

  const fetchIssues = useCallback(
    async (status: string, page?: number) => {
      await dispatch(
        doGetIssues({
          status,
          page,
          take: 10,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      await Promise.all([fetchIssues('active'), fetchIssues('resolved')]);
    };

    fetchInitialData();
  }, [fetchIssues]);

  const selectedIssueFromState = useSelector((state) =>
    selectedIssue ? selectIssueById(state, selectedIssue.issueId) : null,
  );

  const handleSelectIssue = async (issue) => {
    const res = await dispatch(doGetIssue({ id: issue.issueId }));
    setSelectedIssue(res.payload);
    history.push({
      pathname: location.pathname,
      search: `?issueId=${issue.id}`,
    });
    dispatch(doMarkAsSeen({ issueId: issue.id }));
  };

  const handleMarkAllSeen = () => {
    dispatch(doMarkAllAsSeen({ status: currentTab }));
  };

  const handleScroll = () => {
    const scrollElement = scrollContainerRef.current?.querySelector('[data-radix-scroll-area-viewport]');

    if (!scrollElement || isFetchingIssues) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollElement;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      if (pagination.currentPage < pagination.totalPages) {
        fetchIssues(currentTab, pagination.currentPage + 1);
      }
    }
  };

  //   useEffect(() => {
  //     const params = new URLSearchParams(location.search);
  //     const issueIdFromUrl = params.get('issueId');

  //     console.log(issueIdFromUrl);
  //     console.log(activeIssues);

  //     if (issueIdFromUrl) {
  //       console.log('ehllo');
  //       // ✅ Select issue if found in state
  //       const foundIssue =
  //         activeIssues.find((issue) => issue.id === Number(issueIdFromUrl)) ||
  //         resolvedIssues.find((issue) => issue.id === Number(issueIdFromUrl));

  //       console.log(foundIssue);

  //       if (foundIssue) {
  //         setSelectedIssue(foundIssue);
  //         // Ensure tab switches accordingly
  //         setCurrentTab(foundIssue.status === 'RESOLVED' ? 'resolved' : 'active');
  //       } else {
  //         dispatch(doGetIssue({ id: issueIdFromUrl }));
  //       }
  //     }
  //   }, [location.search, activeIssues, resolvedIssues]);

  useEffect(() => {
    const scrollElement = scrollContainerRef.current?.querySelector('[data-radix-scroll-area-viewport]');

    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [scrollContainerRef, isFetchingIssues]);

  useEffect(() => {
    if (!selectedIssueFromState) return;
    setSelectedIssue(selectedIssueFromState);
  }, [selectedIssueFromState]);

  return (
    <div className="bg-white h-[calc(100vh-54px)] overflow-hidden">
      <div className="flex justify-center px-4 py-10">
        <div className="w-full max-w-screen-xl bg-white">
          {/* Top Bar */}
          <div className="flex justify-between items-center">
            <div className="text-left">
              <h1 className="text-2xl font-semibold">Issues</h1>
              <p className="text-sm text-muted-foreground">
                An overview of all your issues. Select an issue to view the conversation
              </p>
            </div>
          </div>
          <Separator className="mt-4 mb-4" />

          <div className="flex ">
            {/* Sidebar - Fixed height with scrollable issues */}
            <div className="w-1/3 border-r p-6">
              {/* Tabs inside the left panel but right-aligned */}
              <div className="flex justify-between mb-4">
                {(currentTab === 'active' && activeUnseenCount > 0) ||
                (currentTab === 'resolved' && resolvedUnseenCount > 0) ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline" size="icon" onClick={() => handleMarkAllSeen()}>
                          <CheckCheck />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Mark as seen</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : null}

                <div className="flex justify-end w-full">
                  <Tabs defaultValue="active" value={currentTab} onValueChange={setCurrentTab} className="w-auto">
                    <TabsList className="flex justify-end">
                      <TabsTrigger value="active">
                        {activeUnseenCount > 0 && activeIssuesPagination.totalItems > 0 && (
                          <span className="h-2 w-2 bg-blue-500 rounded-full animate-pulse mr-2" />
                        )}
                        {`Active (${activeIssuesPagination.totalItems})`}
                      </TabsTrigger>
                      <TabsTrigger value="resolved">
                        {resolvedUnseenCount > 0 && resolvedIssuesPagination.totalItems > 0 && (
                          <span className="h-2 w-2 bg-blue-500 rounded-full animate-pulse mr-2" />
                        )}
                        {`Resolved (${resolvedIssuesPagination.totalItems})`}
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </div>
              <Spin spinning={isFetchingIssues}>
                <ScrollArea
                  className="h-[calc(100vh-270px)] overflow-y-auto pr-2"
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                >
                  {displayedIssues
                    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
                    .map((issue) => (
                      <Card
                        key={issue.id}
                        className={`mb-2 cursor-pointer ${selectedIssue?.id === issue.id ? 'bg-gray-100' : ''} hover:bg-gray-50`}
                        onClick={() => handleSelectIssue(issue)}
                      >
                        <CardContent className="p-4 flex gap-4">
                          {issue.carrierId ? (
                            <Bike className="p-1 rounded-full self-start bg-neutral-400" color="white" />
                          ) : (
                            <img
                              src={issue.companyLogoThumbUrl}
                              alt={issue.companyName}
                              className="h-5 w-5 rounded-full self-start"
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <p className="text-xs text-gray-400">{issue.creatorUserName}</p>
                              <div className="flex items-center">
                                {!issue.hasSeen && (
                                  <span className="h-2 w-2 bg-blue-500 rounded-full animate-pulse mr-2" />
                                )}
                                <p className="text-xs text-gray-500">{formatLastUpdated(issue.updatedAt)}</p>
                              </div>
                            </div>
                            <h3 className="text-md font-medium mt-1">{formatReason(issue.reason)}</h3>
                            <p className="text-sm text-gray-700 mt-1 line-clamp-2 overflow-hidden text-ellipsis">
                              {issue.message}
                            </p>
                            <div className="mt-2 flex gap-2">
                              <Badge variant="outline">
                                <Hash className="h-3 w-3 mr-1" />
                                {issue.orderId2}
                              </Badge>
                              {issue.orderRef && (
                                <Badge variant="outline">
                                  <IdCard className="h-3 w-3 mr-1" /> {issue.orderRef}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </ScrollArea>
              </Spin>
            </div>
            {/* Conversation Panel - Fixed height with scrollable chat */}

            <div className="w-2/3 p-6 flex flex-col ">
              {selectedIssue ? (
                <>
                  {/* related issues */}
                  <h2 className="font-bold mb-2">Related Issues:</h2>
                  <div className="flex justify-between">
                    <div className="flex flex-wrap mb-4">
                      {selectedIssue?.related?.map((m) => {
                        return (
                          <RelatedIssue
                            key={m.issueId}
                            issue={m}
                            selected={selectedIssue.id === m.id}
                            onClick={(issue) => handleSelectIssue(issue)}
                          />
                        );
                      })}
                    </div>
                    {[ROLES.ADMIN, ROLES.COMPANY_OWNER].includes(role) && (
                      <>
                        <Button variant="outline" className="mb-4" onClick={() => setReportIssueModalOpen(true)}>
                          <Plus />
                        </Button>

                        <Modal
                          width={552}
                          title="Report an Issue"
                          open={reportIssueModalOpen}
                          onCancel={() => setReportIssueModalOpen(false)}
                        >
                          <ReportIssueForm
                            issue={selectedIssue}
                            userId={user?.id}
                            onCancel={() => setReportIssueModalOpen(false)}
                            onSuccess={(issue) => {
                              handleSelectIssue(issue);
                              setReportIssueModalOpen(false);
                            }}
                          />
                        </Modal>
                      </>
                    )}
                  </div>
                  <Separator className="my-2" />
                  <IssueChat selectedIssue={selectedIssue} userId={userId} />
                </>
              ) : (
                <p className="text-gray-500 text-center">Select an issue to view the conversation.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssuesPage;

export function formatReason(reason: string) {
  return (
    reason.charAt(0).toUpperCase() + reason.slice(1).toLowerCase().replace(/[_-]/g, ' ') // Replace underscores and dashes with spaces
  ); // Capitalize the first character
}

export function formatPriority(priority: string) {
  switch (priority) {
    case 'HIGH':
      return '🔥 High';
    case 'MEDIUM':
      return 'Medium';
    case 'LOW':
      return 'Low';
    default:
      return priority;
  }
}
